import DatePicker from "@via/components/date-picker/date-picker";
import SelectComponent from "@via/components/select/select";
import { isObject } from "@via/utils/helpers";
import { SelectOption } from "@via/utils/interfaces";
import { DateValueType } from "react-tailwindcss-datepicker";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import { tokenTypeOptions } from "@via/pages/account/profile/integration-tokens/utils/helper";

type FilterItem = { id: string; value: unknown };

type IntegrationTokenFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const IntegrationTokenFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
}: IntegrationTokenFilterProps) => {
  const [debouncedTokenNameFilter, setDebouncedTokenNameFilter] =
    useDebounceValue(
      (columnFilters.find(filter => filter.id === "name")?.value as string) ||
        "",
      300
    );
  const [debouncedOwnerFilter, setDebouncedOwnerFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "ownerEmail")
      ?.value as string) || "",
    300
  );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleChangeDatePickerValue = (
    id: string,
    value: DateValueType | string | null
  ) => {
    // This component only uses range mode (asSingle not set), so value is always DateValueType
    if (typeof value === "string" || value === null) return;

    if (value?.startDate === null && value?.endDate === null) {
      handleValueChange(id, value);
    } else {
      handleValueChange(id, {
        startDate: `${value?.startDate} 00:00:00`,
        endDate: `${value?.endDate} 23:59:59`,
      });
    }
  };

  const handleValueChange = (id: string, value: unknown) => {
    let newFilters = columnFilters.map(filter =>
      filter.id === id ? { ...filter, value } : filter
    );

    if (!columnFilters.find(filter => filter.id === id) && value !== "") {
      newFilters = [...newFilters, { id, value }];
    }

    if (typeof value === "object" && Array.isArray(value) && value.length > 0) {
      newFilters = newFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
    }

    if (isObject(value) && "startDate" in value && "endDate" in value) {
      if (value.startDate !== null && value.endDate !== null) {
        newFilters = newFilters.map(filter =>
          filter.id === id ? { ...filter, value } : filter
        );
      } else {
        newFilters = newFilters.filter(filter => filter.id !== id);
      }
      setColumnFilters(() => newFilters);
    }

    if (id === "name") {
      setDebouncedTokenNameFilter(value as string);
    }
    if (id === "ownerEmail") {
      setDebouncedOwnerFilter(value as string);
    }
    if (id === "tokenType") {
      if (!value) {
        newFilters = newFilters.filter(filter => filter.id !== id);
      }
      setColumnFilters(() => newFilters);
    }
    setPage(0);
    setPageSize(10);
  };

  return (
    <>
      <div className="mt-2 flex-grow">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          defaultValue={debouncedTokenNameFilter}
          onChange={e => debounced("name", e.target.value)}
        />
      </div>
      <div className="mt-2 flex-grow">
        <SelectComponent
          name="tokenType"
          placeholder="Filter By Token Type"
          options={tokenTypeOptions}
          formatOptionLabel={option => (
            <div className="flex items-center gap-2">
              <img className="w-5 py-2" alt={option.label} src={option.icon} />
              <span>{option.label}</span>
            </div>
          )}
          value={tokenTypeOptions.find(
            ({ value }) =>
              columnFilters.find(filter => filter.id === "tokenType")?.value ===
              value
          )}
          onChange={val =>
            handleValueChange("tokenType", (val as SelectOption)?.value)
          }
        />
      </div>
      <div className="mt-2 flex-grow">
        <input
          type="text"
          placeholder="Filter By Owner"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          defaultValue={debouncedOwnerFilter}
          onChange={e => debounced("ownerEmail", e.target.value)}
        />
      </div>
      <div className="mt-2 flex-grow">
        <DatePicker
          placeholder="Filter By Date Created"
          popoverDirection="down"
          value={
            columnFilters.find(filter => filter.id === "createdAt")
              ?.value as DateValueType
          }
          onChange={value => handleChangeDatePickerValue("createdAt", value)}
        />
      </div>
    </>
  );
};

export default IntegrationTokenFilter;
